import { useAuth } from "@/contexts/use-auth";
import type { ChatSendError, ChatThread } from "../types";
import { AIChatComposer } from "./AIChatComposer";
import { AIChatMessageList } from "./AIChatMessageList";
import { AIChatSidebar } from "./AIChatSidebar";
import { AIChatWelcome } from "./AIChatWelcome";

type Props = {
  threads: ChatThread[];
  activeThreadId: string | null;
  historyQuery: string;
  draft: string;
  isGenerating: boolean;
  isHistoryLoading: boolean;
  isSessionLoading?: boolean;
  sendError?: ChatSendError | null;
  onRetrySend?: () => void;
  onHistoryQueryChange: (value: string) => void;
  onSelectThread: (id: string) => void;
  onNewChat: () => void;
  onDraftChange: (value: string) => void;
  onSend: () => void;
};

export function AIChatLayout({
  threads,
  activeThreadId,
  historyQuery,
  draft,
  isGenerating,
  isHistoryLoading,
  isSessionLoading = false,
  sendError = null,
  onRetrySend,
  onHistoryQueryChange,
  onSelectThread,
  onNewChat,
  onDraftChange,
  onSend,
}: Props) {
  const { displayName } = useAuth();
  const activeThread = threads.find((t) => t.id === activeThreadId) ?? null;
  const isEmpty = !activeThread || activeThread.messages.length === 0;
  const composer = (
    <AIChatComposer
      value={draft}
      isEmptyState={isEmpty}
      disabled={isGenerating}
      onChange={onDraftChange}
      onSend={onSend}
    />
  );

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden bg-white">
      <AIChatSidebar
        threads={threads}
        activeThreadId={activeThreadId}
        historyQuery={historyQuery}
        onHistoryQueryChange={onHistoryQueryChange}
        onSelectThread={onSelectThread}
        onNewChat={onNewChat}
        isHistoryLoading={isHistoryLoading}
      />
      <section className="flex min-w-0 flex-1 flex-col bg-white">
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-8 px-4">
            <AIChatWelcome displayName={displayName} />
            <div className="w-full">{composer}</div>
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {isSessionLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#6B7280]">
                  Loading conversation…
                </div>
              ) : (
                <AIChatMessageList
                  messages={activeThread.messages}
                  isGenerating={isGenerating}
                  sendError={sendError}
                  onRetrySend={onRetrySend}
                />
              )}
            </div>
            {composer}
          </>
        )}
      </section>
    </div>
  );
}
